import { ButtonProtected } from '@components'

import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useMarkBankLineAsPrivate } from '@features/reconciliation/hooks'
import { invalidateReconciliationQueries } from '@features/reconciliation/utils'

import { queryClient } from '../../../../../../../../config/queryClient'
import { Scope } from '../../../../../../../../enums/scope'
import { TrackingContext } from '../../../../../../../../enums/trackingContext'
import { useBankTransactionDrawer, useSandboxModeContext } from '../../../../contexts'

interface MarkAsPrivateButtonProps {
  onSuccess?: () => void
}

export const MarkAsPrivateButton = ({ onSuccess }: MarkAsPrivateButtonProps): ReactElement => {
  const { t } = useTranslation()
  const {
    hasBankReconciliationWriteScope,
    selectedTransaction,
    setMatchError,
    clearAllItems,
    setIsPrivate,
    updateSelectedTransaction,
  } = useBankTransactionDrawer()
  const sandbox = useSandboxModeContext()

  const { markAsPrivate, isProcessing } = useMarkBankLineAsPrivate({
    onSuccess: (data) => {
      clearAllItems()
      setMatchError(null)
      setIsPrivate(false)
      invalidateReconciliationQueries(queryClient)

      if (selectedTransaction && data.bankLines && data.bankLineMatches) {
        const matchedBankLine = data.bankLines.find((line) => line.id === selectedTransaction.id)
        const matchedBankLineMatch = data.bankLineMatches[0]

        if (matchedBankLine && matchedBankLineMatch) {
          updateSelectedTransaction({ ...matchedBankLine, match: matchedBankLineMatch })
        }
      }

      onSuccess?.()
    },
    onError: (errorMessage) => {
      setMatchError(errorMessage)
    },
  })

  const handleClick = useCallback(() => {
    if (!selectedTransaction) {
      return
    }

    setMatchError(null)
    if (sandbox) {
      const response = sandbox.matchBankLineInSandbox(selectedTransaction.id, [])
      clearAllItems()
      setIsPrivate(false)

      const matchedBankLine = response.bankLines?.find((line) => line.id === selectedTransaction.id)
      const matchedBankLineMatch = response.bankLineMatches?.[0]
      if (matchedBankLine && matchedBankLineMatch) {
        updateSelectedTransaction({ ...matchedBankLine, match: matchedBankLineMatch })
      }
      onSuccess?.()
      return
    }

    markAsPrivate([selectedTransaction.id])
  }, [
    selectedTransaction,
    setMatchError,
    sandbox,
    markAsPrivate,
    clearAllItems,
    setIsPrivate,
    updateSelectedTransaction,
    onSuccess,
  ])

  return (
    <ButtonProtected
      fullWidth
      disabled={hasBankReconciliationWriteScope && !selectedTransaction}
      loading={isProcessing}
      onClick={handleClick}
      scopes={Scope.BankReconciliationWrite}
      trackingContext={TrackingContext.BankTransactions}
    >
      {t('bank_transactions.drawer.mark_as_private_button')}
    </ButtonProtected>
  )
}
